import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Button,
  Container,
  Divider,
  Form,
  Header,
  Icon,
  Label,
} from 'semantic-ui-react'
import { DateTime } from 'luxon'
import {
  performManualReconciliation,
  autoReconCUBK,
  retryAutoReconCUBK,
  CUBKAutoReconStatus,
} from '@/actions/admin/accountReconciliationActions'
import { fetchUserFinancialAccounts } from '@/actions/admin/adminFinancialAccountActions'
import {
  FinancialAccountWithAdminInfo,
  Reconciliation,
} from '@/reducers/admin/financialAccountsReducer'
import {
  formatCurrency,
  centsToDollars,
  dollarsToCents,
} from '@/utils/currencyHelpers'
import { logHeardError } from '@/utils/errorHelpers'
import { DatePicker, Input, Text, Modal } from '@/componentsV1/BaseComponents'
import { Alert, Checkbox } from '@/ui'
import { convertUtcToLocalDate, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { selectBookkeepingReportsById } from '@/selectors/bookkeepingReportsSelectors'
import { getUserFinancialAccountById } from '@/selectors/financeSelectors'
import { ENABLE_ADMIN, useAnalyticsTrack } from '@/features/amplitude'
import { BookkeepingReport } from '@/reducers/finances/bookkeepingReportsReducer'
import { findPreviousReconciliation } from '../../user-transactions-panels/helpers'
import { useAutoReconCUBK } from '../shared'
import {
  endingDateBounds,
  formatReportMonth,
  isDayInReportMonth,
  localYesterday,
  parseWrongPeriodRejection,
  wrongPeriodMessage,
  type WrongPeriodRejection,
} from '../wrongPeriod'
import { defaultCloseDate, findStartGap } from '../continuity'
import { isAxiosError } from 'axios'

export interface ReconciliationModalProps {
  account: FinancialAccountWithAdminInfo
  close: () => void
  open: boolean
  formType: string
  onSumMatch: () => void
  userId: number
  bookkeepingReport: BookkeepingReport
  toggleVirtualStatement: (reconciliationId?: number) => Promise<void>
  currentReconciliation: Reconciliation | null
}

const FORM_FIELD_PADDING = '1em 2em 0em 2em'

const CASCADE_NOT_RETRYABLE_MESSAGE =
  "Auto-reconciliation ran but couldn't complete. Open its incomplete reconciliation in the books to recover it, or reconcile those months manually."

const CASCADE_RETRYABILITY_UNKNOWN_MESSAGE =
  "Auto-reconciliation couldn't complete, and the account couldn't be refreshed to check whether it can be retried. Reload the page to see how to recover it."

const CLOSE_IN_MONTH_RULE =
  'A statement is filed under the bookkeeping month it closes in.'

/** Which of the three situations the picker is in, said in the bookkeeper's terms. */
const closeInMonthGuidance = ({
  reportMonth,
  surfacedRowMonth,
  supersedesOtherMonth,
  exemptClose,
  periodStart,
  monthAvailable,
}: {
  reportMonth: string
  surfacedRowMonth: string
  supersedesOtherMonth: boolean
  exemptClose: DateTime | null
  periodStart: DateTime | null
  monthAvailable: boolean
}) => {
  const monthName = formatReportMonth(reportMonth)

  if (supersedesOtherMonth) {
    return `${CLOSE_IN_MONTH_RULE} This one closes in ${formatReportMonth(surfacedRowMonth)}, so it stays filed there; entering an ending date in ${monthName} moves it to these books.`
  }

  const straddles =
    exemptClose !== null &&
    !isDayInReportMonth(exemptClose, reportMonth) &&
    !isDayInReportMonth(exemptClose, surfacedRowMonth)

  if (!straddles) {
    return `${CLOSE_IN_MONTH_RULE} These books accept an ending date in ${monthName}; if your statement closes later, reconcile it from the following month's books.`
  }

  const alternative = monthAvailable
    ? `, or any date inside ${monthName}.`
    : `. No date inside ${monthName} is offered, because this period starts ${periodStart?.toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)}; move the starting date back first to re-file it.`

  return `${CLOSE_IN_MONTH_RULE} This one is filed under ${monthName} but closes ${exemptClose.toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)}, so these books accept that closing date${alternative}`
}

const ReconciliationModal = ({
  account,
  close,
  open,
  formType,
  onSumMatch,
  userId,
  bookkeepingReport,
  toggleVirtualStatement,
  currentReconciliation,
}: ReconciliationModalProps) => {
  const previousReconciliation = useMemo(
    () =>
      findPreviousReconciliation(bookkeepingReport, account.reconciliations),
    [bookkeepingReport, account.reconciliations]
  )

  const [startDate, setStartDate] = useState<DateTime | null>(null)
  const [calculatedStartDate, setCalculatedStartDate] =
    useState<DateTime | null>(null)
  const [startAmount, setStartAmount] = useState('0')
  const [endDate, setEndDate] = useState<DateTime | null>(null)
  const [calculatedEndDate, setCalculatedEndDate] = useState<DateTime | null>(
    null
  )
  const [endAmount, setEndAmount] = useState('0')
  const [loading, setLoading] = useState(false)
  const [wrongPeriod, setWrongPeriod] = useState<WrongPeriodRejection | null>(
    null
  )
  const [reconciled, setReconciled] = useState(false)
  const [cascadeMessage, setCascadeMessage] = useState<string | null>(null)
  const [retryabilityUnknown, setRetryabilityUnknown] = useState(false)
  const [retryLoading, setRetryLoading] = useState(false)
  const [reReconcileConfirmed, setReReconcileConfirmed] = useState(false)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const track = useAnalyticsTrack(ENABLE_ADMIN)

  const {
    isEligible,
    autoReconPeriod,
    autoReconPeriodFormattedShort,
    autoReconPeriodFormattedLong,
    loading: cubkLoading,
  } = useAutoReconCUBK({
    userId,
    account,
    bookkeepingReport,
  })

  // The `account` prop is a snapshot taken when the modal opened; the store holds
  // the server's answer after a refetch.
  const liveAccount = useReselector(
    getUserFinancialAccountById,
    userId,
    account.id
  )
  const cubkCascadeRetryable =
    !retryabilityUnknown &&
    Boolean((liveAccount ?? account).cubkCascadeRetryable)
  const cascadeAlert = useMemo(() => {
    if (cascadeMessage === null) return null
    if (retryabilityUnknown) return CASCADE_RETRYABILITY_UNKNOWN_MESSAGE
    if (!cubkCascadeRetryable) return CASCADE_NOT_RETRYABLE_MESSAGE
    return cascadeMessage
  }, [cascadeMessage, retryabilityUnknown, cubkCascadeRetryable])

  // A failed refetch leaves the store's pre-failure flag in place, so it can't be trusted.
  const refreshCascadeRetryability = useCallback(async () => {
    const refreshed = await dispatch(fetchUserFinancialAccounts(userId, true))
    setRetryabilityUnknown(!refreshed)
  }, [dispatch, userId])

  const autoReconCUBKEnabled = useMemo(() => {
    return Boolean(
      isEligible &&
      autoReconPeriod &&
      !currentReconciliation &&
      formType === 'never_reconciled'
    )
  }, [isEligible, autoReconPeriod, currentReconciliation, formType])

  const { plaidInstitutionName: institution, mask, name: accountName } = account
  // Nothing seeds the starting fields on a period with no predecessor, so claiming a
  // prefill there invites submitting the placeholder as an opening balance.
  const startPrefilled = Boolean(
    currentReconciliation ?? previousReconciliation
  )

  const incomplete = formType === 'incomplete'

  // Keyed on status, not formType, so it cannot appear on a first-time or incomplete period.
  const isSettledPeriod = currentReconciliation?.status === 'complete'

  // Read by both the copy and the write, so the two cannot name different months.
  const enteredCloseMonth = endDate?.isValid
    ? endDate.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH)
    : null
  const closesInViewedMonth =
    enteredCloseMonth === null || enteredCloseMonth === bookkeepingReport.date
  const targetBookkeepingReportId = closesInViewedMonth
    ? bookkeepingReport.id
    : (currentReconciliation?.bookkeepingReportId ?? bookkeepingReport.id)

  const surfacedReport = useReselector(
    selectBookkeepingReportsById,
    currentReconciliation?.bookkeepingReportId ?? 0
  )
  const surfacedRowMonth = surfacedReport?.date ?? bookkeepingReport.date
  // Only a write aimed at the surfaced row's own report replaces it; anything else files
  // a new reconciliation and leaves that row alone.
  const supersedesSurfacedRow =
    isSettledPeriod &&
    currentReconciliation?.bookkeepingReportId === targetBookkeepingReportId
  const supersedesOtherMonth =
    supersedesSurfacedRow && surfacedRowMonth !== bookkeepingReport.date

  // Overlap runs both ways and the server treats both identically: a backdated start
  // reaches into an earlier period, a long window into a later one.
  const overlappedCompletePeriods = useMemo(() => {
    if (!startDate?.isValid || !endDate?.isValid) {
      return []
    }
    const from = startDate.startOf('day')
    const to = endDate.startOf('day')
    return account.reconciliations
      .filter(
        (recon) =>
          recon.status === 'complete' && recon.id !== currentReconciliation?.id
      )
      .map((recon) => ({
        start: DateTime.fromISO(recon.startingBalanceDate, { zone: 'utc' }),
        end: DateTime.fromISO(recon.endingBalanceDate, { zone: 'utc' }),
      }))
      .filter(
        ({ start, end }) =>
          start.startOf('day') <= to && end.startOf('day') >= from
      )
      .sort((a, b) => a.start.toMillis() - b.start.toMillis())
  }, [startDate, endDate, account.reconciliations, currentReconciliation?.id])

  const overlapWarning = useMemo(() => {
    if (overlappedCompletePeriods.length === 0) {
      return null
    }
    // Each range already contains commas, so they separate on semicolons or the list reads
    // as one run of dates.
    const ranges = overlappedCompletePeriods
      .map(
        ({ start, end }) =>
          `${start.toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)} to ${end.toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)}`
      )
      .join('; ')
    const many = overlappedCompletePeriods.length > 1
    const subject = many ? 'these reconciled periods' : 'the reconciled period'
    const consequence = many
      ? 'those periods will need reconciling again'
      : 'that period will need reconciling again'
    return `This period overlaps ${subject}: ${ranges}. We will try to reconcile it without disturbing that work. If the numbers do not allow it, ${consequence}.`
  }, [overlappedCompletePeriods])

  const title = reconciled
    ? 'Reconciliation Complete'
    : supersedesSurfacedRow
      ? 'Re-reconcile a Completed Period'
      : incomplete
        ? 'Resume a Reconciliation'
        : 'Begin a Reconciliation'
  const instruction = reconciled
    ? 'This period has been reconciled!'
    : startPrefilled
      ? 'Enter the ending details as they appear on the bank statement'
      : 'Enter both starting and ending details as they appear on the bank statement'
  // Only a period with no reconciliation of its own prefills from the previous one.
  const prefillSource = isSettledPeriod
    ? 'current (complete)'
    : incomplete
      ? 'current (incomplete)'
      : 'previous'
  const startDetails = startPrefilled
    ? `— Auto populated from the ${prefillSource} reconciliation`
    : ''
  const endDetails =
    incomplete || isSettledPeriod
      ? `— Auto populated from the ${prefillSource} reconciliation`
      : ''
  // One value per render: two calls in one expression could straddle midnight.
  const yesterday = localYesterday()

  // A row already filed under a month it does not close in keeps that close: the server
  // exempts a write that leaves it where it is, so the picker has to offer it.
  const exemptClose = convertUtcToLocalDate(
    currentReconciliation?.endingBalanceDate ?? null
  )
  const periodStart = convertUtcToLocalDate(startDate)
  const startGap = useMemo(
    () =>
      findStartGap({
        startDate,
        reconciliations: account.reconciliations,
        excludeReconciliationId: currentReconciliation?.id,
      }),
    [startDate, account.reconciliations, currentReconciliation?.id]
  )
  // Several closes, not the predecessor alone: February clamps a 29th or 30th billing day
  // to the 28th, which on its own cannot be told from a true month-end account.
  const settledCloses = useMemo(
    () =>
      account.reconciliations
        .filter(
          (recon) =>
            recon.status === 'complete' &&
            recon.id !== currentReconciliation?.id &&
            recon.endingBalanceDate < bookkeepingReport.startDate
        )
        .map((recon) =>
          DateTime.fromISO(recon.endingBalanceDate, { zone: 'utc' })
        ),
    [
      account.reconciliations,
      currentReconciliation?.id,
      bookkeepingReport.startDate,
    ]
  )
  // Both months are reachable: a close inside the viewed month files here, one inside the
  // surfaced row's month retargets to that row's report. Either passes the server's rule.
  const {
    latest: latestSelectableEnd,
    isSelectable: isSelectableEndDate,
    monthAvailable,
    hasSelectable,
  } = endingDateBounds({
    reportMonths: [bookkeepingReport.date, surfacedRowMonth],
    periodStart,
    exemptClose,
  })
  const reportMonthName = formatReportMonth(bookkeepingReport.date)
  const closeMonthGuidance = closeInMonthGuidance({
    reportMonth: bookkeepingReport.date,
    surfacedRowMonth,
    supersedesOtherMonth,
    exemptClose,
    periodStart,
    monthAvailable,
  })

  const resetForm = useCallback(() => {
    setStartDate(null)
    setStartAmount('0')
    setEndDate(null)
    setEndAmount('0')
    setLoading(false)
    setReconciled(false)
    setWrongPeriod(null)
    setCascadeMessage(null)
    setRetryLoading(false)
    // Intent must be declared for the submit that carries it, never inherited.
    setReReconcileConfirmed(false)
  }, [])

  useEffect(() => {
    if (!open) {
      resetForm()
      return
    }

    if (!currentReconciliation) {
      const priorClose = previousReconciliation
        ? DateTime.fromISO(previousReconciliation.endingBalanceDate, {
            zone: 'utc',
          })
        : null

      if (priorClose && previousReconciliation) {
        const newStartDate = priorClose.plus({ days: 1 })
        const newStartAmount = centsToDollars(
          previousReconciliation.endingBalanceInCents
        )

        setStartDate(newStartDate)
        setCalculatedStartDate(newStartDate)
        setStartAmount(newStartAmount.toString())
      } else {
        const sd = DateTime.fromISO(bookkeepingReport.startDate, {
          zone: 'utc',
        })
        setStartDate(sd)
        setCalculatedStartDate(sd)
      }
      // Seeding month end on a card that bills mid-cycle pins the sequence to calendar
      // months, because the next period's start chains off whatever this close becomes.
      const ed = defaultCloseDate({
        closes: settledCloses,
        reportMonth: bookkeepingReport.date,
        reportEnd: DateTime.fromISO(bookkeepingReport.endDate, { zone: 'utc' }),
        today: localYesterday().setZone('utc').startOf('day'),
      })
      setEndDate(ed)
      setCalculatedEndDate(ed)
      return
    }

    let startDate: DateTime | null = null
    let startAmount = 0
    let endDate: DateTime | null = null
    let endAmount = 0

    // Settled periods seed too: the re-reconcile UI promises that prefill, and without
    // it a confirmed replace would write zeroes over real balances.
    const seedsFromOwnRow =
      formType === 'needs_updating' ||
      formType === 'incomplete' ||
      isSettledPeriod

    // Seeding from the period's own row keeps a reconciliation inside its period.
    if (currentReconciliation && seedsFromOwnRow) {
      startDate = DateTime.fromISO(currentReconciliation.startingBalanceDate, {
        zone: 'utc',
      })
      startAmount = centsToDollars(currentReconciliation.startingBalanceInCents)
      endDate = DateTime.fromISO(currentReconciliation.endingBalanceDate, {
        zone: 'utc',
      })
      endAmount = centsToDollars(currentReconciliation.endingBalanceInCents)
    }

    setStartDate(startDate)
    setCalculatedStartDate(startDate)
    setStartAmount(startAmount.toString())
    setEndDate(endDate)
    setCalculatedEndDate(endDate)
    setEndAmount(endAmount.toString())
  }, [
    open,
    account.type,
    formType,
    currentReconciliation,
    previousReconciliation,
    isSettledPeriod,
    resetForm,
    bookkeepingReport.startDate,
    bookkeepingReport.endDate,
    bookkeepingReport.date,
    settledCloses,
  ])

  const handleError = useCallback((err: unknown) => {
    // A wrong-period rejection is correctable input, so it gets a named month and a
    // link in the form rather than a browser alert of server prose.
    const rejection = parseWrongPeriodRejection(err)
    if (rejection) {
      setWrongPeriod(rejection)
      setLoading(false)
      return
    }
    // Surface the server's own message for conflicts (process mismatch, closed
    // books, …) so each 409 states its real cause and recovery.
    if (isAxiosError(err)) {
      alert(err.response?.data.message)
    } else {
      alert(err)
    }
    setLoading(false)
  }, [])

  const onSubmit = useCallback(async () => {
    if (
      typeof startAmount === 'undefined' ||
      !startDate?.isValid ||
      typeof endAmount === 'undefined' ||
      !endDate?.isValid
    ) {
      if ((startDate && !startDate.isValid) || (endDate && !endDate.isValid)) {
        logHeardError('ReconciliationModal: invalid reconciliation date', {
          startDateValid: startDate?.isValid ?? null,
          endDateValid: endDate?.isValid ?? null,
        })
      }
      alert('Please make sure all fields are filled in with valid dates')
      return
    }

    const message = `\nQuick check before getting underway — Do these dates and figures look correct?
    
    ${[institution, accountName, mask].filter(Boolean).join(' ')}:

      Started on ${startDate.toFormat(DATE_FORMATS_LUXON.INPUT)} with ${formatCurrency(startAmount)}
      Ended on ${endDate.toFormat(DATE_FORMATS_LUXON.INPUT)} with ${formatCurrency(endAmount)}
    `
    const result = confirm(message)

    if (result) {
      setLoading(true)

      try {
        if (autoReconCUBKEnabled) {
          const cubkResponse = await autoReconCUBK(account.id, {
            bookkeepingReportId: bookkeepingReport.id,
            startingBalanceDate: startDate.toFormat(
              DATE_FORMATS_LUXON.YEAR_MONTH_DAY
            ),
            startingBalanceInCents: dollarsToCents(Number(startAmount)),
            endingBalanceDate: endDate.toFormat(
              DATE_FORMATS_LUXON.YEAR_MONTH_DAY
            ),
            endingBalanceInCents: dollarsToCents(Number(endAmount)),
          })(dispatch)

          if (!cubkResponse) {
            throw new Error('Error attempting to reconcile with CUBK')
          }

          if (cubkResponse.status === CUBKAutoReconStatus.ManualReconFailed) {
            navigate(
              `${location.pathname}/reconciliation/${cubkResponse.manualReconciliation.id}?bookkeepingReportId=${bookkeepingReport.id}`
            )
            setLoading(false)
            return
          }

          if (
            cubkResponse.status === CUBKAutoReconStatus.CascadeFailed ||
            cubkResponse.status === CUBKAutoReconStatus.Failed
          ) {
            // The manual reconciliation committed even though the cascade didn't.
            // Only the server knows whether the cascade row exists, which decides retry.
            await refreshCascadeRetryability()
            setReconciled(true)
            setCascadeMessage(
              cubkResponse.message ??
                'Auto-reconciliation could not complete. Please retry.'
            )
            setLoading(false)
            return
          }

          setReconciled(true)
          onSumMatch()

          // wait 3 seconds to show the user the manual reconcilation is complete
          await new Promise((resolve) => setTimeout(resolve, 3000))
          if (cubkResponse.reconciliation) {
            await toggleVirtualStatement(cubkResponse.reconciliation.id)
            close()
          }
        } else {
          const startingBalanceInCents = dollarsToCents(Number(startAmount))
          const endingBalanceInCents = dollarsToCents(Number(endAmount))
          const startingBalanceDate = startDate.toFormat(
            DATE_FORMATS_LUXON.YEAR_MONTH_DAY
          )
          const endingBalanceDate = endDate.toFormat(
            DATE_FORMATS_LUXON.YEAR_MONTH_DAY
          )

          const { reconciliation, outcome } = await performManualReconciliation(
            account.id,
            {
              bookkeepingReportId: targetBookkeepingReportId,
              startingBalanceDate,
              startingBalanceInCents,
              endingBalanceDate,
              endingBalanceInCents,
              // The writer resolves the lane before it reads intent, so sending the
              // wrong one discards the intent rather than refusing it.
              bookkeepingProcess:
                currentReconciliation?.bookkeepingProcess ?? 'monthEnd',
              // A blanket true would strip settled rows of their protection.
              ...(supersedesSurfacedRow && reReconcileConfirmed
                ? { supersedeSettled: true }
                : {}),
            }
          )(dispatch)

          // `reuseSettled` means the writer kept an existing settled row untouched —
          // nothing we submitted was applied, so don't report success.
          if (outcome === 'reuseSettled') {
            alert(
              'This period is already reconciled, so no changes were applied.'
            )
            setLoading(false)
            return
          }

          const doMatch = reconciliation.status === 'complete'
          const reconAnalyticsAttributes = {
            reconciliationId: reconciliation.id,
            accountId: account.id,
            startDate: startDate.toFormat(DATE_FORMATS_LUXON.INPUT),
            endDate: endDate.toFormat(DATE_FORMATS_LUXON.INPUT),
            startDateChanged:
              calculatedStartDate === null ||
              !startDate.equals(calculatedStartDate),
            endDateChanged:
              calculatedEndDate === null || !endDate.equals(calculatedEndDate),
            startBalanceChanged:
              dollarsToCents(Number(startAmount)) !==
              reconciliation.startingBalanceInCents,
          }

          if (!doMatch) {
            track('Reconciliation Break', reconAnalyticsAttributes)
            navigate(
              `${location.pathname}/reconciliation/${reconciliation.id}?bookkeepingReportId=${bookkeepingReport.id}`
            )
          } else {
            setReconciled(true)
            onSumMatch()
            setLoading(false)
            track('Reconciliation Complete', reconAnalyticsAttributes)
          }
        }
      } catch (err) {
        handleError(err)
      }
    }
  }, [
    account.id,
    accountName,
    dispatch,
    endAmount,
    endDate,
    calculatedEndDate,
    institution,
    mask,
    currentReconciliation,
    onSumMatch,
    startAmount,
    startDate,
    calculatedStartDate,
    location.pathname,
    navigate,
    track,
    bookkeepingReport.id,
    targetBookkeepingReportId,
    toggleVirtualStatement,
    autoReconCUBKEnabled,
    close,
    handleError,
    supersedesSurfacedRow,
    reReconcileConfirmed,
    refreshCascadeRetryability,
  ])

  const handleRetryAutoReconCUBK = useCallback(async () => {
    setRetryLoading(true)
    try {
      const result = await retryAutoReconCUBK(account.id)(dispatch)

      if (!result) {
        throw new Error('Error retrying CUBK auto-reconciliation')
      }

      if (result.status === CUBKAutoReconStatus.Success) {
        // The retry outcome has no reconciliation object of its own to display, so a
        // forced refetch (bypassing the fetch-if-needed cache) is what makes the new
        // cascade row visible and clears `cubkCascadeRetryable` — without it the
        // table and this button stay stale until a full remount.
        await dispatch(fetchUserFinancialAccounts(userId, true))
        onSumMatch()
        close()
        return
      }

      await refreshCascadeRetryability()
      setCascadeMessage(
        result.message ??
          'Auto-reconciliation could not complete. Please retry.'
      )
    } catch (err) {
      handleError(err)
    } finally {
      setRetryLoading(false)
    }
  }, [
    account.id,
    dispatch,
    onSumMatch,
    close,
    userId,
    handleError,
    refreshCascadeRetryability,
  ])

  const reconciliationComplete = useMemo(
    () => (
      <Container style={{ textAlign: 'center' }}>
        {cascadeAlert && (
          <Alert
            variant="warning"
            className="mb-4"
            style={{ textAlign: 'left' }}
          >
            {cascadeAlert}
          </Alert>
        )}
        <h2 style={{ padding: '2em' }}>{instruction}</h2>
        <Icon color="green" size="massive" name="check circle" />
        {autoReconCUBKEnabled && !cascadeMessage && (
          <Text as="bodySm" style={{ marginTop: 16 }}>
            Running auto-reconciliation for{' '}
            <b>{autoReconPeriodFormattedLong}</b>
          </Text>
        )}
      </Container>
    ),
    [
      instruction,
      autoReconCUBKEnabled,
      autoReconPeriodFormattedLong,
      cascadeMessage,
      cascadeAlert,
    ]
  )

  return (
    <Modal open={open}>
      <Modal.Header as="h3">{title}</Modal.Header>
      {!reconciled && (
        <Modal.Content>
          {cascadeAlert && (
            <Alert
              variant="warning"
              className="mb-4"
              style={{ textAlign: 'left' }}
            >
              {cascadeAlert}
            </Alert>
          )}
          <Header>{instruction}</Header>

          <h4>
            {institution}
            <br />
            {accountName}: {mask}
          </h4>
          <Divider />

          <Form>
            {wrongPeriod && (
              <Alert variant="error" className="mb-4">
                {wrongPeriodMessage(wrongPeriod)}{' '}
                {wrongPeriod.suggestedReportId !== null && (
                  <Link
                    to={`/admin/finances/records/${userId}/monthly-books/${wrongPeriod.suggestedReportId}`}
                    // The panel owns this modal's lifetime, so navigating alone
                    // leaves it mounted over the destination month.
                    onClick={close}
                  >
                    Go to {formatReportMonth(wrongPeriod.closeMonth)} books
                  </Link>
                )}
              </Alert>
            )}

            {overlapWarning && (
              <Alert variant="warning" className="mb-4">
                {overlapWarning}
              </Alert>
            )}

            {isSettledPeriod && !supersedesSurfacedRow && (
              <Alert variant="default" className="mb-4">
                The reconciliation shown here is filed under{' '}
                {formatReportMonth(surfacedRowMonth)} and closes in{' '}
                {formatReportMonth(enteredCloseMonth ?? bookkeepingReport.date)}
                . Submitting files a new reconciliation under{' '}
                {formatReportMonth(bookkeepingReport.date)} and leaves{' '}
                {formatReportMonth(surfacedRowMonth)}&apos;s unchanged.
              </Alert>
            )}

            {supersedesSurfacedRow && (
              <>
                <Alert variant="warning" className="mb-2">
                  {supersedesOtherMonth ? (
                    <>
                      {formatReportMonth(bookkeepingReport.date)} has no
                      reconciliation of its own. It is covered by one filed
                      under {formatReportMonth(surfacedRowMonth)}, and
                      submitting updates that reconciliation rather than adding
                      one here.
                    </>
                  ) : (
                    <>
                      {formatReportMonth(bookkeepingReport.date)} is already
                      reconciled. Re-reconciling replaces the dates and balances
                      recorded on that reconciliation rather than adding a new
                      one.
                    </>
                  )}
                </Alert>
                <div className="mb-4">
                  <Checkbox
                    checked={reReconcileConfirmed}
                    onCheckedChange={setReReconcileConfirmed}
                    label={`Yes, replace the completed reconciliation for ${formatReportMonth(surfacedRowMonth)}`}
                  />
                </div>
              </>
            )}

            <Alert
              variant={hasSelectable ? 'default' : 'error'}
              className="mb-4"
              description={
                hasSelectable
                  ? closeMonthGuidance
                  : `No ending date is available. This period starts ${periodStart?.toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)}, which is after ${reportMonthName} ends, so move the starting date back or reconcile from the books for the month it closes in.`
              }
            />

            <Label color="green" ribbon>
              Starting
              <Label.Detail>{startDetails}</Label.Detail>
            </Label>

            <Form.Field style={{ padding: FORM_FIELD_PADDING }}>
              <DatePicker
                label="Date"
                disabled={loading}
                maxDate={(endDate
                  ? convertUtcToLocalDate(endDate)
                  : yesterday
                )?.toJSDate()}
                placeholder="Starting Balance Date"
                value={startDate?.toFormat(DATE_FORMATS_LUXON.INPUT) ?? ''}
                onChange={(value) => {
                  setWrongPeriod(null)
                  setStartDate(
                    DateTime.fromFormat(value, DATE_FORMATS_LUXON.INPUT, {
                      zone: 'utc',
                    })
                  )
                }}
              />
            </Form.Field>
            {startGap && (
              <Alert
                variant="warning"
                className="mb-2"
                description={`${startGap.firstUncoveredDay.toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)} to ${startGap.lastUncoveredDay.toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)} would be covered by no reconciliation, because the last completed period on this account closed ${startGap.priorClose.toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)}. Open this period the day after that close unless the account genuinely has no statement for those ${startGap.uncoveredDays === 1 ? 'day' : `${startGap.uncoveredDays} days`}.`}
              />
            )}
            <Form.Field style={{ padding: FORM_FIELD_PADDING }}>
              <Input
                label="Amount (USD $)"
                componentType="currency"
                value={startAmount}
                onChange={setStartAmount}
                disabled={loading}
                placeholder="Starting Balance Amount"
              />
            </Form.Field>

            <Label color="red" ribbon>
              Ending
              <Label.Detail style={{ color: 'white' }}>
                {endDetails}
              </Label.Detail>
            </Label>
            <Form.Field style={{ padding: FORM_FIELD_PADDING }}>
              <DatePicker
                label="Date"
                disabled={loading}
                minDate={periodStart?.toJSDate()}
                maxDate={latestSelectableEnd.toJSDate()}
                filterDate={isSelectableEndDate}
                placeholder="Ending Balance Date"
                value={endDate?.toFormat(DATE_FORMATS_LUXON.INPUT) ?? ''}
                onChange={(value) => {
                  setWrongPeriod(null)
                  setEndDate(
                    DateTime.fromFormat(value, DATE_FORMATS_LUXON.INPUT, {
                      zone: 'utc',
                    })
                  )
                }}
              />
            </Form.Field>
            <Form.Field style={{ padding: FORM_FIELD_PADDING }}>
              <Input
                label="Amount (USD $)"
                componentType="currency"
                value={endAmount}
                onChange={setEndAmount}
                disabled={loading}
                placeholder="Ending Balance Amount"
              />
            </Form.Field>
          </Form>
        </Modal.Content>
      )}
      {reconciled && reconciliationComplete}
      <Modal.Actions>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
            width: '100%',
            gap: 8,
          }}
        >
          {autoReconCUBKEnabled && !reconciled && (
            <Text as="bodySm">
              <b>NOTE:</b> Successfully completing this manual reconciliation
              will trigger auto-reconciliation for:{' '}
              {autoReconPeriodFormattedShort}
            </Text>
          )}

          {cubkCascadeRetryable && (
            <Button
              basic
              color="orange"
              loading={retryLoading}
              disabled={loading || retryLoading}
              onClick={handleRetryAutoReconCUBK}
            >
              Retry auto-reconciliation
            </Button>
          )}

          <Button
            basic
            disabled={
              loading ||
              retryLoading ||
              cubkLoading ||
              (reconciled && autoReconCUBKEnabled && !cascadeMessage)
            }
            onClick={close}
          >
            {`${reconciled ? 'Close' : 'Cancel'}`}
          </Button>
          {!reconciled && (
            <Button
              primary
              loading={loading}
              disabled={
                loading ||
                !hasSelectable ||
                (supersedesSurfacedRow && !reReconcileConfirmed)
              }
              onClick={onSubmit}
            >
              {supersedesSurfacedRow
                ? 'Re-reconcile'
                : incomplete
                  ? 'Resume'
                  : 'Begin'}
            </Button>
          )}
        </div>
      </Modal.Actions>
    </Modal>
  )
}

export default ReconciliationModal
